
//src/pages/ProductRecommendation.js 
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import {
  Container, Typography, Button, Card, CardContent, TextField,
  FormControl, FormGroup, FormControlLabel, Checkbox, InputAdornment,
  Grid, Accordion, AccordionSummary, AccordionDetails, CircularProgress,
  Box, RadioGroup, Radio, Paper, ToggleButton, ToggleButtonGroup,
  Slider, Autocomplete, Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CardActionArea,
} from '@mui/material';
import {
  ExpandMore,
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday as CalendarTodayIcon,
  Note as NoteIcon,
  Timeline as TimelineIcon,
  FilterAlt as FilterAltIcon,
  RestartAlt as RestartAltIcon,
  InputSharp,
  CurrencyRupee
} from '@mui/icons-material';
import { ThumbUpAlt, ThumbDownAlt } from '@mui/icons-material';

import { motion, transform } from 'framer-motion';
import ProgressContext from '../context/ProgressContext';
import { UserContext } from '../context/UserContext';
import { data, useNavigate } from 'react-router-dom';
import HorizontalStepper from '../components/HorizontalStepper';
import DOMPurify from 'dompurify';
import BackButton from '../components/BackButton';
import productTypes from '../data/productTypes.json';
import apiClient from '../utils/apiClient';
import apiClient4 from '../utils/apiClient4';
import { range } from '@mui/x-data-grid/internals';
import { Modal, IconButton } from '@mui/material';
import ThumbDownAltOutlined from '@mui/icons-material/ThumbDownAltOutlined'
import ThumbUpAltOutlined from '@mui/icons-material/ThumbUpAltOutlined'
import CryptoJS from 'crypto-js';




/* ───────────────── helpers ───────────────── */
const num = (v, d = 0) => (isNaN(parseFloat(v)) ? d : +v);
/** stable stringify (sorted keys) so two equal objects produce identical keys */
const stableStringify = (value) => {
  const seen = new WeakSet();
  const _s = (v) => {
    if (v === null || typeof v !== 'object') return JSON.stringify(v);
    if (seen.has(v)) return '"[Circular]"';
    seen.add(v);
    if (Array.isArray(v)) return '[' + v.map(_s).join(',') + ']';
    const keys = Object.keys(v).sort();
    return '{' + keys.map(k => JSON.stringify(k) + ':' + _s(v[k])).join(',') + '}';
  };
  return _s(value);
};
/** merge recommendation rows from multiple APIs; keep variants; drop only truly identical rows */
const mergeRecs = (prev = [], next = []) => {
  const p = Array.isArray(prev) ? prev : [];
  const n = Array.isArray(next) ? next : [];
  const map = new Map();
  [...p, ...n].forEach(item => {
    if (!item) return;
    const { _source, ...rest } = item;          // exclude local tag from identity 
    const key = stableStringify(rest);
    if (!map.has(key)) map.set(key, item);      // first in wins (order stable) 
  });
  return Array.from(map.values());
};
/** merge & dedupe segment-data rows by Product Code (preferring first seen) */
const mergeSeg = (prev = [], next = []) => {
  const pArr = Array.isArray(prev) ? prev : [];
  const nArr = Array.isArray(next) ? next : [];
  const map = new Map();
  [...pArr, ...nArr].forEach(item => {
    if (!item) return;
    const code = item['Product Code'] ?? item.product_code ?? item.product_name ?? stableStringify(item);
    if (!map.has(code)) map.set(code, item);
  });
  return Array.from(map.values());
};
/** merge & dedupe errors by product_code + benefit_type + message */
const mergeErrors = (prev = [], next = []) => {
  const pArr = Array.isArray(prev) ? prev : [];
  const nArr = Array.isArray(next) ? next : [];
  const map = new Map();
  [...pArr, ...nArr].forEach(e => {
    if (!e) return;
    const key = [
      e.product_code ?? e['Product Code'] ?? '',
      e.benefit_type ?? '',
      e.error_message ?? e.message ?? ''
    ].join('::');
    if (!map.has(key)) map.set(key, e);
  });
  return Array.from(map.values());
};
/** prefer the longer non-empty HTML (or keep previous) */
const pickHtml = (prevHtml, nextHtml) => {
  const p = (prevHtml || '').trim();
  const n = (nextHtml || '').trim();
  if (!p) return n;
  if (!n) return p;
  return n.length > p.length ? n : p;
};
/** safe getter for "risk profile" field embedded per-row (if present) */
const getRowRisk = (r) => {
  const raw = r?.risk_profile ?? r?.new_risk_profile ?? r?.risk ?? '';
  return typeof raw === 'string' ? raw : String(raw || '');
};
const RISK_OPTIONS = ['Conservative', 'Moderate', 'Aggressive'];
export default function ProductRecommendation() {
  /* ────────────── context / nav ────────────── */
  const { steps, currentStep, nextStep, setCurrentStep } = useContext(ProgressContext);
  const navigate = useNavigate();
  const { setUserNTID, setUserChannel, userNTID } = useContext(UserContext);
  const {
    segmentName,setSegmentName,
    agentStrategy, setAgentStrategy,
    customerNeeds, setCustomerNeeds,
    selectedProducts: selProd, setSelectedProducts,
    selectedProductCodes: selCd, setSelectedProductCodes,
    setPitchResponse,
    productRecommendationFormData, setProductRecommendationFormData,
    recommendations = {}, setRecommendations,
    recommendations2 = {}, setRecommendations2,
    pitchResponse,
    segmentData, setSegmentData,
    segmentData2, setSegmentData2,
    setProducatComparison
  } = useContext(UserContext);

  const savedSegmentation = JSON.parse(
    localStorage.getItem('formDataSegmentation_history') || '{}'
  );
  
  const defaultForm = {
    getBenefitsAs: 'Income',
    lumpsumTimeline: 5,
    lumpsumAmount: '',
    incomeStartYear: 1,
    incomeTerm: 5,
    incomeAmountPa: '',
    riskProfile: 'Conservative',
    additionalInfo: '',
    customerName: '',
    customerAge: '',
    gender: 'Male',
    customerIncome: '',
    dependents: '',
    rop: '100',
  };
  
  /* ────────────── form state ────────────── */
  const [formData, setFormData] = useState(() => ({
    ...defaultForm,
    ...productRecommendationFormData,
    ...savedSegmentation,
  }));

  

  const [formData2, setFormData2] = useState(() => ({
    ...defaultForm,
    ...productRecommendationFormData,
    ...savedSegmentation,
  }));

  console.log("productRecommendationFormData--->", productRecommendationFormData);

  console.log("formData2---->", formData2);

  console.log("formDataSegmentation_history--->", localStorage.getItem("formDataSegmentation_history"));


  const [modalOpen, setModalOpen] = useState(false)  
  const handleOpen = () => setModalOpen(true)
  const handleClose = () => setModalOpen(false)
const CLIENT_ID = process.env.REACT_APP_LOGINASSISTANT_HASH;
// EXACT encryption logic you provided:
function encryptAES(plainText) {
  const key = CryptoJS.enc.Utf8.parse(CLIENT_ID);
  // Last 16 characters for IV
  const ivString = CLIENT_ID.slice(-16);
  const iv = CryptoJS.enc.Utf8.parse(ivString);
  const encrypted = CryptoJS.AES.encrypt(
    CryptoJS.enc.Utf8.parse(plainText),
    key,
    {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    }
  );
  return encrypted.toString(); // Base64
}

  useEffect(() => setProductRecommendationFormData(formData), [formData, setProductRecommendationFormData]);
  /* ────────────── step indicator ────────────── */
  useEffect(() => {
    const idx = steps.findIndex(s => s.path === '/dashboard/product-recommendation');
    if (idx !== -1) setCurrentStep(idx);
  }, [steps, setCurrentStep]);
  /* ────────────── async flags ────────────── */
  const [loading, setLoading] = useState(false); // disables “Generate” button 
  const [pitchLoading, setPitchLoading] = useState(false); // disables “Proceed” button 
  const [pendingRequests, setPendingReqs] = useState(0);     // drives “Loading more…” 
  const [pendingRequests2, setPendingReqs2] = useState(0);     // drives “Loading more…”   
  const firstSuccessSetNeeds = useRef(false);
  /* ────────────── selections ────────────── */
  const [selectedProductsState, setSelProdState] = useState(selProd || []);
  const [selectedProductCodesState, setSelCdState] = useState(selCd || []);
  const dislikeFeedback = async () =>{
    if (!feedback_liked && !feedback_disliked) {
      setFeedback_open(1)
    }
  }

  useEffect(() => {
    setSelectedProducts(selectedProductsState);
    setSelectedProductCodes(selectedProductCodesState);
  }, [selectedProductsState, selectedProductCodesState, setSelectedProducts, setSelectedProductCodes]);
  /* ────────────── range limits & filters ────────────── */
  const [rangeLimits, setRangeLimits] = useState({
    ppt: [0, 40],
    term: [0, 50],
    premium: [0, 2_000_000]
  });
  // filtersAuto: true = we auto-expand ranges as new data arrives, until the user touches any filter 
  const [filtersAuto, setFiltersAuto] = useState(true);
  const [filters, setFilters] = useState({
    ppt: [...rangeLimits.ppt],
    term: [...rangeLimits.term],
    premium: [...rangeLimits.premium],
    types: [],
    risk: []  // new risk filter – empty means "all" 
  });
  const [filters2, setFilters2] = useState({
    ppt: [...rangeLimits.ppt],
    term: [...rangeLimits.term],
    premium: [...rangeLimits.premium],
    types: [],
    risk: []  // new risk filter – empty means "all" 
  });

  // recompute limits when the underlying data grows; only reset filters while user hasn't touched them 
  useEffect(() => {
    if(localStorage.getItem('location_history')=='product_recommendation_commited' ){
      setRecommendations(JSON.parse(localStorage.getItem('recomendations')))
      try{
      setFormData(JSON.parse(localStorage.getItem('formData_history')))
      setSegmentName(localStorage.getItem('segmentname_history'))
      }catch(e){
        console.log("here is the error: ", e)
        console.log(localStorage.getItem('formData_history'))
      }
      localStorage.removeItem('location_history')
    }

    const recs = recommendations.recommendations || [];
    if (!Array.isArray(recs) || recs.length === 0) return;
    const minMax = key => {
      const arr = recs.map(r => num(r[key], NaN)).filter(v => !isNaN(v));
      if (!arr.length) return [0, 0];
      return [Math.min(...arr), Math.max(...arr)];
    };
    const lim = {
      ppt: minMax('premium_payment_term'),
      term: minMax('policy_term'),
      premium: minMax('annual_premium')
    };
    setRangeLimits(lim);
    if (filtersAuto) {
      setFilters(f => ({
        ...f,
        ppt: [...lim.ppt],
        term: [...lim.term],
        premium: [...lim.premium],
        // keep types & risk selections as-is (both empty by default = show all) 
      }));
    }
  }, [recommendations, filtersAuto]);
  // prevent back button leaving chat (keeps behaviour you had)
  // useEffect(() => {
  //   const handlePopState = () => window.history.pushState(null, "", window.location.href);
  //   window.history.pushState(null, "", window.location.href);
  //   window.addEventListener("popstate", handlePopState);
  //   return () => window.removeEventListener("popstate", handlePopState);
  // }, []);
  /* reset button */
  const resetFilters = () => {
    setFiltersAuto(true); // allow auto-expansion again on subsequent loads 
    setFilters({
      ppt: [...rangeLimits.ppt],
      term: [...rangeLimits.term],
      premium: [...rangeLimits.premium],
      types: [],
      risk: []
    });
  };
  /* filtered + sorted list (ASC by annual premium) */
  const filteredRecs = useMemo(() => {
    const recs = Array.isArray(recommendations.recommendations) ? recommendations.recommendations : [];
    return recs
      .filter(r => {
        const pptOK = num(r.premium_payment_term) >= filters.ppt[0] && num(r.premium_payment_term) <= filters.ppt[1];
        const termOK = num(r.policy_term) >= filters.term[0] && num(r.policy_term) <= filters.term[1];
        const premOK = num(r.annual_premium) >= filters.premium[0] && num(r.annual_premium) <= filters.premium[1];
        const t = productTypes[r.product_code ?? r['Product Code'] ?? ''] || 'Unknown';
        const typeOK = !filters.types.length || filters.types.includes(t);
        const rowRisk = getRowRisk(r);
        const riskOK = !filters.risk.length || filters.risk.some(opt => opt.toLowerCase() === rowRisk.toLowerCase());
        return pptOK && termOK && premOK && typeOK && riskOK;
      })
      .sort((a, b) => num(a.annual_premium) - num(b.annual_premium)); // ASC 
  }, [recommendations, filters]);


  const filteredRecs2 = useMemo(() => {
    console.log("here first",recommendations )
    const recs = Array.isArray(recommendations2.recommendations) ? recommendations2.recommendations : [];
    console.log('here second', recs)
    return recs
      .filter(r => {
        const pptOK = num(r.premium_payment_term) >= filters2.ppt[0] && num(r.premium_payment_term) <= filters2.ppt[1];
        const termOK = num(r.policy_term) >= filters2.term[0] && num(r.policy_term) <= filters2.term[1];
        const premOK = num(r.annual_premium) >= filters2.premium[0] && num(r.annual_premium) <= filters2.premium[1];
        const t = productTypes[r.product_code ?? r['Product Code'] ?? ''] || 'Unknown';
        const typeOK = !filters2.types.length || filters2.types.includes(t);
        const rowRisk = getRowRisk(r);
        const riskOK = !filters2.risk.length || filters2.risk.some(opt => opt.toLowerCase() === rowRisk.toLowerCase());
        return pptOK && termOK && premOK && typeOK && riskOK;
      })
      .sort((a, b) => num(a.annual_premium) - num(b.annual_premium)); // ASC 
  }, [recommendations2, filters]);


  /* single / mix toggle */
  const [chosenType, setChosenType] = useState(agentStrategy.recommendation_type || '1.1');
  const singleExists = !!recommendations.single_product_recommendation;
  const mixExists = !!recommendations.product_mix_recommendation;
  const showChoice = singleExists && mixExists;
  /* ────────────── handlers ────────────── */
  const onField = k => (e, v) => { if (k == 'getBenefitsAs') { resetFilters(); } setFormData({ ...formData, [k]: typeof v !== 'undefined' ? v : e.target.value }) };
  const onField2 = k => (e, v) => { if (k == 'getBenefitsAs') { resetFilters(); } setFormData2({ ...formData2, [k]: typeof v !== 'undefined' ? v : e.target.value }) };

  const onRadio = e => setFormData({ ...formData, riskProfile: e.target.value });
  const onRecType = t => {
    setChosenType(t);
    setAgentStrategy(a => ({ ...a, recommendation_type: t }));
  };
  const onProductSelect = (e, p) => {
    const { name, checked } = e.target;
    if (checked) {
      setSelProdState(pr => [...pr, name]);
      setSelCdState(cd => [...cd, p['Product Code']]);
    } else {
      setSelProdState(pr => pr.filter(x => x !== name));
      setSelCdState(cd => cd.filter(x => x !== p['Product Code']));
    }
  };
  // filter UI hooks 
  const slidePpt = (_, v) => { setFiltersAuto(false); setFilters(f => ({ ...f, ppt: v })); };
  const slideTerm = (_, v) => { setFiltersAuto(false); setFilters(f => ({ ...f, term: v })); };
  const slidePremium = (_, v) => { setFiltersAuto(false); setFilters(f => ({ ...f, premium: v })); };
  const changeTypes = (_, v) => { setFiltersAuto(false); setFilters(f => ({ ...f, types: v })); };
  const changeRisk = (_, v) => { setFiltersAuto(false); setFilters(f => ({ ...f, risk: v })); };

  const handleSliderFocusChange = (e, index, rangeLimits, obj, is_focus) => {
    let calculated_value = e.target.value
    if (is_focus) {
      console.log("nirmalya====", is_focus, calculated_value, rangeLimits[0], rangeLimits[1])

      if (calculated_value > rangeLimits[1]) {
        calculated_value = rangeLimits[1]
      } if (calculated_value < rangeLimits[0]) {
        calculated_value = rangeLimits[0]
      }
    }
    if (obj == 'ppt') {
      const newObj = [...filters.ppt];
      newObj[index] = Number(calculated_value)
      setFilters({ ...filters, ppt: newObj });
    } else if (obj == 'term') {
      const newObj = [...filters.term];
      newObj[index] = Number(calculated_value)
      setFilters({ ...filters, term: newObj });
    } else if (obj == 'premium') {
      const newObj = [...filters.premium];
      newObj[index] = Number(calculated_value)
      setFilters({ ...filters, premium: newObj });
    }
  }

  /* ────────────── API calls ────────────── */
  const generateRecommendation = async () => {
    setLoading(true);
    setPendingReqs(0);
    setPendingReqs2(0);
    firstSuccessSetNeeds.current = false;
    // wipe previous data (but don't touch user's chosen filters) 
    setRecommendations({
      recommendations: [],
      errors: [],
      single_product_recommendation: '',
      product_mix_recommendation: ''
    });
    setRecommendations2({
      recommendations: [],
      errors: [],
      single_product_recommendation: '',
      product_mix_recommendation: ''
    })
    setSegmentData([]);

    const segmentationHistory = JSON.parse(localStorage.getItem('formDataSegmentation_history')) || {};
    
    const needs = {
      customer_name: formData2.customerName || segmentationHistory.customerName || '',
      customer_age: num(formData2.customerAge || segmentationHistory.customerAge),
      gender: formData2.gender || segmentationHistory.gender || 
      'Male',
      customer_income: num(formData2.customerIncome || segmentationHistory.customerIncome),
      dependents: num(formData.dependents),
      get_benefits_as: formData.getBenefitsAs,
      lumpsum_timeline: num(formData.lumpsumTimeline),
      lumpsum_amount: num(formData.lumpsumAmount),
      income_start_year: num(formData.incomeStartYear) > 0 ? num(formData.incomeStartYear) - 1 : 0,
      income_term: num(formData.incomeTerm),
      income_amount_pa: num(formData.incomeAmountPa),
      new_risk_profile: formData.riskProfile,
      additional_info: formData.additionalInfo || '',
    };

    const payload = {
      customer_needs: needs,
      agent_strategy: agentStrategy,
      segment_name: segmentName,
      ntID: userNTID,
      user_id: userNTID,
    };

    const endpoints = [
      { url: '/product-recommendation', tag: 'api1' },
      { url: '/product-recommendation_1', tag: 'api2' },
      { url: '/product-recommendation_2', tag: 'api3' },
      { url: '/product-recommendation_3', tag: 'api4' }
    ];

    setPendingReqs(endpoints.length);
    endpoints.forEach(async ({ url, tag }) => {
      try {
        const { data } = await apiClient.post(url, payload, { withCredentials: true });
        console.log("data====", data)
        // 1) recommendations (tag with _source so we can display/debug if needed) 
        const list = Array.isArray(data.recommendations) ? data.recommendations.map(r => ({ ...r, _source: tag })) : [];
        // 2) errors 
        const errs = Array.isArray(data.errors) ? data.errors : [];
        // 3) segment 
        const seg = Array.isArray(data.segment_data) ? data.segment_data : [];
        setRecommendations(prev => ({
          ...prev,
          recommendations: mergeRecs(prev.recommendations, list),
          errors: mergeErrors(prev.errors, errs),
          single_product_recommendation: pickHtml(prev.single_product_recommendation, data.single_product_recommendation),
          product_mix_recommendation: pickHtml(prev.product_mix_recommendation, data.product_mix_recommendation)
        }));
        if (seg.length) {
          setSegmentData(prev => mergeSeg(prev, data.recommendations.map((i) => {
            return {
              "Cumulitive": 1,
              "Product Benifit": "",
              "Product Code": i.product_code,
              "Product Name": i.product_name,
              "purpose": "",
              "Type of Products": ""
            }
          })
          ));
        }
        if (!firstSuccessSetNeeds.current) {
          setCustomerNeeds(needs);
          firstSuccessSetNeeds.current = true;
        }
      } catch (err) {
        console.error(`Error calling ${url}`, err);
      } finally {
        setPendingReqs(prev => {
          const next = prev - 1;
          if (next === 0) setLoading(false);  // all finished 
          return next;
        });
      }
    });

    if (condition_added == 1) {
      const segmentationHistory = JSON.parse(localStorage.getItem('formDataSegmentation_history')) || {};

      const needs2 = {
        customer_name: formData2.customerName || segmentationHistory.customerName || '',
        customer_age: num(formData2.customerAge || segmentationHistory.customerAge),
        gender: formData2.gender || segmentationHistory.gender || 
        'Male',
        customer_income: num(formData2.customerIncome || segmentationHistory.customerIncome),
        dependents: num(formData2.dependents),
        get_benefits_as: formData2.getBenefitsAs,
        lumpsum_timeline: num(formData2.lumpsumTimeline),
        lumpsum_amount: num(formData2.lumpsumAmount),
        income_start_year: num(formData2.incomeStartYear) > 0 ? num(formData2.incomeStartYear) - 1 : 0,
        income_term: num(formData2.incomeTerm),
        income_amount_pa: num(formData2.incomeAmountPa),
        new_risk_profile: formData2.riskProfile,
        additional_info: formData2.additionalInfo || '',
      };

      console.log("NEEDS2--->",needs2)
      console.log("formDATA2",formData2)
      console.log("savedSegmentation", savedSegmentation);

      const payload2 = {
        customer_needs: needs2,
        agent_strategy: agentStrategy,
        segment_name: segmentName,
        ntID: userNTID
      };

      setPendingReqs2(endpoints.length);
      endpoints.forEach(async ({ url, tag }) => {
        try {
          const { data } = await apiClient.post(url, payload2, { withCredentials: true });
          console.log("here", data)
          // 1) recommendations (tag with _source so we can display/debug if needed) 
          const list = Array.isArray(data.recommendations) ? data.recommendations.map(r => ({ ...r, _source: tag })) : [];
          // 2) errors 
          const errs = Array.isArray(data.errors) ? data.errors : [];
          // 3) segment 
          const seg = Array.isArray(data.segment_data) ? data.segment_data : [];
          setRecommendations2(prev => ({
            ...prev,
            recommendations: mergeRecs(prev.recommendations, list),
            errors: mergeErrors(prev.errors, errs),
            single_product_recommendation: pickHtml(prev.single_product_recommendation, data.single_product_recommendation),
            product_mix_recommendation: pickHtml(prev.product_mix_recommendation, data.product_mix_recommendation)
          }));
          if (seg.length) {
            setSegmentData2(prev => mergeSeg(prev, data.recommendations.map((i) => {
              return {
                "Cumulitive": 1,
                "Product Benifit": "",
                "Product Code": i.product_code,
                "Product Name": i.product_name,
                "purpose": "",
                "Type of Products": ""
              }
            })
            ));
          }
          if (!firstSuccessSetNeeds.current) {
            setCustomerNeeds(needs);
            firstSuccessSetNeeds.current = true;
          }
        } catch (err) {
          console.error(`Error calling ${url}`, err);
        } finally {
          setPendingReqs2(prev => {
            const next = prev - 1;
            if (next === 0) setLoading(false);  // all finished 
            return next;
          });
        }
      });
    }


  };
  const trimBeforeHTMLBlock = (input) => {
    return input.replace(/^[\s\S]*?(?=<\s*[a-zA-Z!/?])/m, '')
  }
  const handleProceedToEBI = async () => {
      nextStep();
            const nxt = steps[currentStep + 1]?.path;
      if (nxt) navigate(nxt);

  };
  const [feedback_open, setFeedback_open] = useState(0)
  const [feedback_liked, setFeedback_licked] = useState(false)
  const [feedback_disliked, setFeedback_dislicked] = useState(false)
  const [feedback_value, feedback_value_set] = useState('')
  const [condition_added, setAddCondition] = useState('')
  const removeLastCondition = () => {
    setAddCondition(0)
  }
  const addNewCondition = () => {
    setAddCondition(1)
  }
  const likeFeedback = async () => {
    if (!feedback_liked) {
      const response = await apiClient.post('/feedback', {
        "session_id": userNTID,
        "query": "EBI Generation",
        "response": "",
        "feedback": "thumbs_up",
        "product": "",
        "ntID": userNTID

      }, { withCredentials: true });

      setFeedback_licked(true)
    }

  }
  const submitFeedback = async () => {
    if (!feedback_disliked) {
      const response = await apiClient.post('/feedback', {
        "session_id": userNTID,
        "query": "EBI Generation",
        "response": feedback_value,
        "feedback": "thumbs_down",
        "product": "",
        "ntID": userNTID
      }, { withCredentials: true });
      FeedbackPopupClose()
      setFeedback_dislicked(true)
    }

  }
  const getUniqueValFromProduct = (product_id) => {
    const newUniqueValue = Math.floor(Math.random() * 900000 + 100000).toString();
    return newUniqueValue
  }
  const openLoginAssistant = (product_code)=>{
    localStorage.setItem('location_history', 'product_recommendation')
    localStorage.setItem('recomendations', JSON.stringify(recommendations))
    localStorage.setItem('formData_history', JSON.stringify(formData))
    localStorage.setItem('segmentname_history', segmentName)
    // The external system wants "DILI" + <uniqueVal> as the raw session_id
    const rawSessionId = 'DILI' + getUniqueValFromProduct(product_code); // e.g. DILI123456
    console.log("rawSessionid ==== ", rawSessionId)
    const encrypted = encryptAES(rawSessionId);
    // Example iFrame URL:
    // http://buyuat.iprulifeinsurance.com/digiuatnew/EBI.htm#/LAEBI/<productCode>?value=<encrypted>
    // const baseUrl = 'https://buyuat.iprulifeinsurance.com/digiuatnew/EBI.htm#/LAEBI';
    const baseUrl = 'https://buy.iciciprulife.com/buy/EBI.htm#/LAEBI';
    // const baseUrl="http://localhost:3000"
    // return baseUrl    

    let la_url = `${baseUrl}/${product_code}?value=${encodeURIComponent(encrypted)}`

    // window.open(la_url);

    console.log("Opening LA website: ", la_url)

    // iOS
    // if (window.webkit?.messageHandlers?.redirect_pdfurl) {
    if (window.webkit) {
      window.webkit.messageHandlers.redirect_url.postMessage(la_url);
      return;
    }
  
    // Android
    // if (window.Android?.redirect_url) {
    if (window.Android) {
      window.Android.redirect_url(la_url);
      return;
    }
    
    // Web fallback
    // window.open(la_url, "_blank");
    window.location.href = la_url;

  }


  const downloadPdfProduct = async (xml_details) =>{
    localStorage.setItem('location_history', 'product_recommendation')
    localStorage.setItem('recomendations', JSON.stringify(recommendations))
    localStorage.setItem('formData_history', JSON.stringify(formData))
    localStorage.setItem('segmentname_history', segmentName)

    xml_details = xml_details.replaceAll('GenerateEBIDigital', 'GenerateEBIDigitalPDF')
    let headers = {
        "Content-Type": "application/json; charset=utf-8",
        // "SOAPAction": "http://tempuri.org/GenerateEBIDigitalPDF",
    }
    //UAT
    // let response = await apiClient4.post('https://api-dex.iciciprulife.com/v1/core/quote', xml_details, headers)
    // let response = await apiClient4.post('https://api-uat.iciciprulife.com/v1/core/quote', xml_details, headers)
    //Prod
    // let response = await apiClient4.post('https://api.iciciprulife.com/v1/core/quote', xml_details, headers)

    const QUOTE_API_URL = process.env.REACT_APP_API_BASE_URLE

    const xml_details_enc = encryptAES(xml_details);

    const payload_xml_details_enc = {"encryptedRequest":xml_details_enc}

    console.log("payload_xml_details_enc",payload_xml_details_enc)

    let response = await apiClient4.post(`${QUOTE_API_URL}/v1/core/quote`, payload_xml_details_enc, headers)
    
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(response.data, "text/xml")

    const resultTag = xmlDoc.getElementsByTagName("GenerateEBIDigitalPDFResult")[0];
    const pdfUrl = resultTag.textContent.trim();

    // window.open(pdfUrl)
    // window.webkit.messageHandlers.test.postMessage("Redirect_PDF|"+pdfUrl);
    // window.webkit.messageHandlers.redirect_pdfurl.postMessage(pdfUrl);

    console.log("Opening PDF: ", pdfUrl)

    // iOS
    // if (window.webkit?.messageHandlers?.redirect_pdfurl) {
    if (window.webkit) {
      window.webkit.messageHandlers.redirect_pdfurl.postMessage(pdfUrl);
      return;
    }
  
    // Android
    // if (window.Android?.redirect_pdfurl) {
    if (window.Android) {
      window.Android.redirect_pdfurl(pdfUrl);
      return;
    }
    
    // Web fallback
    // window.open(pdfUrl, "_blank");
    window.location.href = pdfUrl;

  }


  const FeedbackPopupClose = () => {
    setFeedback_open(0)
  }

  const CompareProductJson = () =>{
    console.log(selectedProductsState)
    console.log(filteredRecs)
    const uniqueNames = [...new Set(filteredRecs.map(item => item.product_name))];
    const filteredDetails = uniqueNames.map(name => filteredRecs.find(item => item.product_name === name))
    console.log(filteredDetails)
    setProducatComparison(filteredDetails)
    handleProceedToEBI()
  }

  const customerPitchOpen = async() =>{
        setPitchLoading(true);
    try {

        // setSelCdState(segmentData.forEach((p)=>{return p['Product Code']})) 
        // setSelProdState(segmentData.forEach((p)=>{return p['Product Names']}))
      const { data } = await apiClient.post(
        '/generate-pitch',
        {
          customer_needs: customerNeeds,
          selected_product_codes: selectedProductCodesState,
          selected_products: selectedProductsState,
          agent_strategy: agentStrategy,
          segment_name: segmentName,
          ntID: userNTID

        },
        { withCredentials: true }
      );
      let pitch_response = trimBeforeHTMLBlock(data.pitch_response)

      setPitchResponse(pitch_response);
      handleOpen()

    } catch (err) { console.error(err); }
    finally { setPitchLoading(false); }

  }

  const toggle_expand_section = (i) =>{
    console.log("a====",document.getElementById('expand_section_'+i).style.display)
    if(document.getElementById('expand_section_'+i).style.display=='block'){
      document.getElementById('expand_section_'+i).style.display='none'
      document.getElementById('toggle_expand_button_'+i).innerHTML='Expand'
      document.getElementById('toggle_expand_button_icon_'+i).style.transform = 'rotate(0deg)'

    }
     else{
      console.log("a====b")
      document.getElementById('expand_section_'+i).style.display='block'
      document.getElementById('toggle_expand_button_'+i).innerHTML='Collapse'
      document.getElementById('toggle_expand_button_icon_'+i).style.transform = 'rotate(180deg)'


    }
  }
  

  const nextStepLabel = steps[currentStep + 1]?.label
    ? `Proceed to ${steps[currentStep + 1].label}` : 'Finish';
  /* ───────────────────────── JSX ───────────────────────── */
  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Modal
        open={feedback_open}
        onClose={() => { setFeedback_open(0) }}
      >
        <div style={{ margin: '250px' }}>
          <div style={{ padding: '20px', backgroundColor: 'white', overflowY: 'scroll', height: '250px', width: '100%', marginLeft: '0' }}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              <h4>Please provide your feedback and opinion. </h4>
            </Typography>
            <TextField minRows={3} sx={{ width: '100%', 'marginBottom': '5' }} placeholder='Tell us your feedback' value={feedback_value} onChange={(e) => { feedback_value_set(e.target.value) }} />

            <Button variant="contained" color="primary" sx={{ float: 'right', marginTop: '15px' }} onClick={submitFeedback}>Submit</Button>
            <Button variant="outlined" color="primary" sx={{ float: 'right', marginTop: '15px', marginRight: '15px' }} onClick={FeedbackPopupClose}>Close</Button>


          </div>
        </div>

      </Modal>
      <Modal open={modalOpen} onClose={handleClose}>
        <div style={{ margin: '100px' }}>
          <div style={{ padding: '20px', backgroundColor: 'white', overflowY: 'scroll', height: '600px' }}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              <h2>Pitch we are giving:
                <Button variant="contained" color="primary" sx={{ float: 'right' }} onClick={handleClose}>Close</Button></h2>

            </Typography>
            <Typography
              sx={{ marginBottom: '60px' }}
              component="div"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(pitchResponse) }}
            />


            <div sx={{ padding: '10px', width: '100%', marginTop: '20px', marginBottom: '60px' }}>
              <IconButton onClick={() => { likeFeedback() }}>
                {!feedback_liked ? (
                  <ThumbUpAltOutlined sx={{ color: "rgb(112 48 160)" }} />
                ) : (
                  <ThumbUpAlt sx={{ color: "rgb(112 48 160)" }} />
                )}
              </IconButton>
              <IconButton onClick={() => { dislikeFeedback() }}>
                {!feedback_disliked ? (
                  <ThumbDownAltOutlined sx={{ color: "rgb(112 48 160)" }} />
                ) : (
                  <ThumbDownAlt sx={{ color: "rgb(112 48 160)" }} />
                )}
              </IconButton>
              <Button variant="contained" color="primary" sx={{ width: '20%', float: 'right', }} onClick={handleClose}>Close</Button>
            </div>

          </div>
        </div>
      </Modal>

      <HorizontalStepper />
      <BackButton />
      <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: .5 }}>
        <Typography variant="h4" sx={{ mt: 4, fontWeight: 'bold' }}>Product Recommendation</Typography>
        {/* ================ FORM CARD ================ */}
        <Card sx={{ mt: 3, bgcolor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(4px)', borderRadius: 4, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" mb={2}>Customize Your Plan</Typography>

            <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid #ddd', borderRadius: 2, bgcolor: '#f9f9f9' }}>
              {/* 1. Benefits */}
              <Paper elevation={1} sx={{ p: 2, mb: 3, border: '1px solid #ddd', borderRadius: 2, bgcolor: '#f9f9f9' }}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>1. Get Benefits As</Typography>
                <ToggleButtonGroup id="type_selection" value={formData.getBenefitsAs} exclusive onChange={onField('getBenefitsAs')} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <ToggleButton value="Income" sx={{ textTransform: 'none', p: 1.5, fontWeight: 'bold' }}>Income</ToggleButton>
                  <ToggleButton value="Lump Sum" sx={{ textTransform: 'none', p: 1.5, fontWeight: 'bold' }}>Lump Sum</ToggleButton>
                </ToggleButtonGroup>
                {formData.getBenefitsAs === 'Lump Sum' && (
                  <Grid container spacing={2} mt={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Timeline (years)" type="number" fullWidth value={formData.lumpsumTimeline}
                        onChange={onField('lumpsumTimeline')}
                        InputProps={{ startAdornment: <InputAdornment position="start"><TimelineIcon /></InputAdornment>, inputProps: { min: 1 } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Benefit Amount (₹)" type="number" fullWidth value={formData.lumpsumAmount}
                        onChange={onField('lumpsumAmount')}
                        InputProps={{ startAdornment: <InputAdornment position="start"><CurrencyRupee /></InputAdornment> }} />
                    </Grid>
                  </Grid>
                )}
                {formData.getBenefitsAs === 'Income' && (
                  <Grid container spacing={2} mt={2}>
                    <Grid item xs={12} sm={3}>
                      <TextField label="Income Start Year" type="number" fullWidth value={formData.incomeStartYear}
                        onChange={onField('incomeStartYear')}
                        InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayIcon /></InputAdornment>, inputProps: { min: 1 } }} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField label="Income Term (years)" type="number" fullWidth value={formData.incomeTerm}
                        onChange={onField('incomeTerm')}
                        InputProps={{ startAdornment: <InputAdornment position="start"><AccessTimeIcon /></InputAdornment>, inputProps: { min: 1 } }} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField label="Income Amount p.a. (₹)" type="number" fullWidth value={formData.incomeAmountPa}
                        onChange={onField('incomeAmountPa')}
                        InputProps={{ startAdornment: <InputAdornment position="start"><CurrencyRupee /></InputAdornment> }} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography variant='body2'>R.O.P %</Typography>
                      <Slider
                      aria-label='R.O.P %'
                      min ={0}
                      max = {200}
                      onChange={onField('rop')}
                      step={100}
                      valueLabelDisplay='auto'
                      marks = {[
                        {value:0, label:'0'},
                        {value:100, label:'100'},
                        {value: 200, label:'200'}
                      ]}
                      />
                    </Grid>
                  </Grid>
                )}
              </Paper>
              {/* 2. Risk */}
              <Paper elevation={1} sx={{ p: 2, mb: 3, border: '1px solid #ddd', borderRadius: 2, bgcolor: '#f9f9f9' }}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>2. Risk Profile</Typography>
                <FormControl component="fieldset">
                  <RadioGroup row value={formData.riskProfile} onChange={onRadio}>
                    {['Conservative', 'Moderate', 'Aggressive'].map(r => (
                      <FormControlLabel key={r} value={r} control={<Radio />} label={r} />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Paper>
              {/* 3. Additional */}
              <Paper elevation={1} sx={{ p: 2, mb: 2, border: '1px solid #ddd', borderRadius: 2, bgcolor: '#f9f9f9', display: 'none' }}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>3. Additional Information</Typography>
                <TextField label="Additional details" multiline rows={4} fullWidth value={formData.additionalInfo}
                  onChange={onField('additionalInfo')}
                  InputProps={{ startAdornment: <InputAdornment position="start"><NoteIcon /></InputAdornment> }} />
              </Paper>
            </Paper>


            {condition_added ? (
              <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid #ddd', borderRadius: 2, bgcolor: '#f9f9f9' }}>
                {/* 1. Benefits */}
                <Paper elevation={1} sx={{ p: 2, mb: 3, border: '1px solid #ddd', borderRadius: 2, bgcolor: '#f9f9f9' }}>
                  <Typography variant="subtitle1" fontWeight="bold" mb={1}>2. Get Benefits As</Typography>
                  <ToggleButtonGroup id="type_selection" value={formData2.getBenefitsAs} exclusive onChange={onField2('getBenefitsAs')} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <ToggleButton value="Income" sx={{ textTransform: 'none', p: 1.5, fontWeight: 'bold' }}>Income</ToggleButton>
                    <ToggleButton value="Lump Sum" sx={{ textTransform: 'none', p: 1.5, fontWeight: 'bold' }}>Lump Sum</ToggleButton>
                  </ToggleButtonGroup>
                  {formData2.getBenefitsAs === 'Lump Sum' && (
                    <Grid container spacing={2} mt={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField label="Timeline (years)" type="number" fullWidth value={formData2.lumpsumTimeline}
                          onChange={onField2('lumpsumTimeline')}
                          InputProps={{ startAdornment: <InputAdornment position="start"><TimelineIcon /></InputAdornment>, inputProps: { min: 1 } }} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField label="Benefit Amount (₹)" type="number" fullWidth value={formData2.lumpsumAmount}
                          onChange={onField2('lumpsumAmount')}
                          InputProps={{ startAdornment: <InputAdornment position="start"><CurrencyRupee /></InputAdornment> }} />
                      </Grid>
                    </Grid>
                  )}
                  {formData2.getBenefitsAs === 'Income' && (
                    <Grid container spacing={2} mt={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField label="Income Start Year" type="number" fullWidth value={formData2.incomeStartYear}
                          onChange={onField2('incomeStartYear')}
                          InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayIcon /></InputAdornment>, inputProps: { min: 1 } }} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField label="Income Term (years)" type="number" fullWidth value={formData2.incomeTerm}
                          onChange={onField2('incomeTerm')}
                          InputProps={{ startAdornment: <InputAdornment position="start"><AccessTimeIcon /></InputAdornment>, inputProps: { min: 1 } }} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField label="Income Amount p.a. (₹)" type="number" fullWidth value={formData2.incomeAmountPa}
                          onChange={onField2('incomeAmountPa')}
                          InputProps={{ startAdornment: <InputAdornment position="start"><CurrencyRupee /></InputAdornment> }} />
                      </Grid>
                    </Grid>
                  )}
                </Paper>
                {/* 2. Risk */}
                <Paper elevation={1} sx={{ p: 2, mb: 3, border: '1px solid #ddd', borderRadius: 2, bgcolor: '#f9f9f9' }}>
                  <Typography variant="subtitle1" fontWeight="bold" mb={1}>2. Risk Profile</Typography>
                  <FormControl component="fieldset">
                    <RadioGroup row value={formData2.riskProfile} onChange={onRadio}>
                      {['Conservative', 'Moderate', 'Aggressive'].map(r => (
                        <FormControlLabel key={r} value={r} control={<Radio />} label={r} />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Paper>
                {/* 3. Additional */}
                <Paper elevation={1} sx={{ p: 2, mb: 2, border: '1px solid #ddd', borderRadius: 2, bgcolor: '#f9f9f9', display: 'none' }}>
                  <Typography variant="subtitle1" fontWeight="bold" mb={1}>3. Additional Information</Typography>
                  <TextField label="Additional details" multiline rows={4} fullWidth value={formData2.additionalInfo}
                    onChange={onField2('additionalInfo')}
                    InputProps={{ startAdornment: <InputAdornment position="start"><NoteIcon /></InputAdornment> }} />
                </Paper>
              </Paper>
            ) : (<></>)}


            {/* {condition_added ? (
              <Button variant="outlined" color="primary" onClick={removeLastCondition} disabled={loading} sx={{ borderRadius: 3, px: 4 }}>
                Remove Last Condition
              </Button>

            ) : (<>
              <Button variant="outlined" color="primary" onClick={addNewCondition} disabled={loading} sx={{ borderRadius: 3, px: 4 }}>
                + Add New Condition
              </Button>

            </>)} */}


            <Box textAlign="right" mt={3}>
              <Button variant="contained" color="primary" onClick={generateRecommendation} disabled={loading} sx={{ borderRadius: 3, px: 4 }}>
                {loading ? <CircularProgress size={24} /> : 'Generate Recommendation'}
              </Button>
            </Box>
          </CardContent>
        </Card>
        {/* ================ FILTER PANEL ================ */}
        {(recommendations.recommendations || []).length > 0 && (
          <Paper elevation={0} sx={{ mt: 6, p: 3, borderRadius: 3, bgcolor: 'grey.100' }}>
            <Box display="flex" alignItems="center" mb={2} gap={1}>
              <FilterAltIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">Filter Recommendations</Typography>
              <Box flexGrow={1} />
              <Button startIcon={<RestartAltIcon />} size="small" onClick={resetFilters}>Reset</Button>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={12}>
                <Typography gutterBottom fontWeight="bold">PPT&nbsp;(yrs)</Typography>
                <Grid container spacing={3} mt={2}>
                  <Grid item xs={3} md={1.3}>
                    <TextField label="Min" type="number" fullWidth value={filters.ppt[0]}
                      onChange={(e) => { handleSliderFocusChange(e, 0, rangeLimits.ppt, 'ppt', 0) }} onBlur={(e) => { handleSliderFocusChange(e, 0, rangeLimits.ppt, 'ppt', 1) }}
                    />
                  </Grid>
                  <Grid item xs={6} md={9.4} mt={2}>
                    <Slider value={filters.ppt} onChange={slidePpt} valueLabelDisplay="auto"
                      min={rangeLimits.ppt[0]} max={rangeLimits.ppt[1]} />
                  </Grid>
                  <Grid item xs={3} md={1.3}>
                    <TextField label="Max" type="number" fullWidth value={filters.ppt[1]}
                      onChange={(e) => { handleSliderFocusChange(e, 1, rangeLimits.ppt, 'ppt', 0) }} onBlur={(e) => { handleSliderFocusChange(e, 1, rangeLimits.ppt, 'ppt', 1) }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <hr />

              <Grid item xs={12} md={12}>
                <Typography gutterBottom sx={{ fontWeight: 'bold' }}>Policy Term&nbsp;(yrs)</Typography>
                <Grid container spacing={3} mt={2}>
                  <Grid item xs={3} md={1.3}>
                    <TextField label="Min" type="number" fullWidth value={filters.term[0]}
                      onChange={(e) => { handleSliderFocusChange(e, 0, rangeLimits.term, 'term', 0) }} onBlur={(e) => { handleSliderFocusChange(e, 0, rangeLimits.term, 'term', 1) }}
                    />
                  </Grid>
                  <Grid item xs={6} md={9.4} mt={2}>
                    <Slider value={filters.term} onChange={slideTerm} valueLabelDisplay="auto"
                      min={rangeLimits.term[0]} max={rangeLimits.term[1]} />
                  </Grid>
                  <Grid item xs={3} md={1.3}>
                    <TextField label="Max" type="number" fullWidth value={filters.term[1]}
                      onChange={(e) => { handleSliderFocusChange(e, 1, rangeLimits.term, 'term', 0) }} onBlur={(e) => { handleSliderFocusChange(e, 1, rangeLimits.term, 'term', 1) }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <hr />


              <Grid item xs={12} md={12}>
                <Typography gutterBottom sx={{ fontWeight: 'bold' }} >Annual Premium&nbsp;(₹)</Typography>
                <Grid container spacing={3} mt={2}>
                  <Grid item xs={12} md={1.3}>
                    <TextField label="Min" type="number" fullWidth value={filters.premium[0]}
                      onChange={(e) => { handleSliderFocusChange(e, 0, rangeLimits.premium, 'premium', 0) }} onBlur={(e) => { handleSliderFocusChange(e, 0, rangeLimits.premium, 'premium', 1) }}
                    />
                  </Grid>
                  <Grid item xs={12} md={9.4} mt={2}>
                    <Slider value={filters.premium} onChange={slidePremium} valueLabelDisplay="auto"
                      min={rangeLimits.premium[0]} max={rangeLimits.premium[1]} />
                  </Grid>
                  <Grid item xs={12} md={1.3}>
                    <TextField label="Max" type="number" fullWidth value={filters.premium[1]}
                      onChange={(e) => { handleSliderFocusChange(e, 1, rangeLimits.premium, 'premium', 0) }} onBlur={(e) => { handleSliderFocusChange(e, 1, rangeLimits.premium, 'premium', 1) }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={12} sx={{ display: 'none' }}>
                <Typography gutterBottom>Risk</Typography>
                <ToggleButtonGroup value={filters.risk} onChange={changeRisk} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {RISK_OPTIONS.map(opt => (
                    <ToggleButton key={opt} value={opt} size="small" sx={{ textTransform: 'none', px: 1.5 }}>
                      {opt}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={12} sx={{ display: 'none' }}>
                <Autocomplete
                  multiple
                  options={[...new Set(Object.values(productTypes))].sort()}
                  value={filters.types}
                  onChange={changeTypes}
                  renderTags={(val, getTagProps) => val.map((opt, i) => <Chip {...getTagProps({ index: i })} key={opt} label={opt} />)}
                  renderInput={params => <TextField {...params} variant="outlined" label="Product Type" />}
                />
              </Grid>
            </Grid>
          </Paper>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} md={condition_added == 1 ? 6 : 12} mt={3} >
            <h1>Product Recommendation</h1>
            <Paper elevation={1} sx={{ p: 2, mb: 1, border: '1px solid #ddd', borderRadius: 2, bgcolor: '#f9f9f9' }}>
              {/* ================ SINGLE / MIX CHOICE ================ */}
              {showChoice && (
                <Box mt={4}>
                  <Typography variant="h6">Please choose Single Product or Product Mix:</Typography>
                  <Box display="flex" gap={2} mt={2}>
                    {[
                      { label: 'Single Product', type: '1.1' },
                      { label: 'Product Mix', type: '1.2' }
                    ].map(btn => (
                      <Card key={btn.type} onClick={() => onRecType(btn.type)}
                        sx={{
                          flex: 1, cursor: 'pointer', borderRadius: 2,
                          border: chosenType === btn.type ? '4px solid #1976d2' : '1px solid grey'
                        }}>
                        <CardContent><Typography fontWeight="bold">{btn.label}</Typography></CardContent>
                      </Card>
                    ))}
                  </Box>
                </Box>
              )}
              {/* ================ SINGLE / MIX CONTENT ================ */}
              {chosenType === '1.1' && singleExists && (
                <Box mt={4}>
                  <Typography variant="h6" mb={2}>Single Product Recommendation</Typography>
                  <Card sx={{ borderRadius: 2 }}><CardContent>
                    <Typography component="div" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(recommendations.single_product_recommendation) }} />
                  </CardContent></Card>
                </Box>
              )}
              {chosenType === '1.2' && mixExists && (
                <Box mt={4}>
                  <Typography variant="h6" mb={2}>Product Mix Recommendation</Typography>
                  <Card sx={{ borderRadius: 2 }}><CardContent>
                    <Typography component="div" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(recommendations.product_mix_recommendation) }} />
                  </CardContent></Card>
                </Box>
              )}
              {/* ================ SYSTEM RECOMMENDATIONS ================ */}
              <Box mt={6}>
                <Typography variant="h5" fontWeight="bold"></Typography>
                {/* Empty state while loading */}
                {pendingRequests > 0 && (filteredRecs.length === 0) && (
                  <Box display="flex" alignItems="center" gap={1} mt={2}>
                    <CircularProgress size={20} /><Typography variant="body2">Loading…</Typography>
                  </Box>
                )}
                {/* No matches after load */}
                {pendingRequests === 0 && filteredRecs.length === 0 && (
                  <Box mt={2}>
                    <Typography variant="body2">No recommendations match the current filters.</Typography>
                    <Button onClick={resetFilters} size="small" sx={{ mt: 1 }}>Reset Filters</Button>
                  </Box>
                )}
                {/* {JSON.stringify(filteredRecs)} */}
                {filteredRecs.filter((p, i, a) => a.filter(x => x.product_code === p.product_code).indexOf(p) < 3).map((rec, i) => (
                  <Card key={i} sx={{ mb: 2 }}>
                    <CardContent>

                      {/* <FormControlLabel key={rec['product_code']}
                          control={<Checkbox checked={selectedProductsState.includes(rec['product_code'])}
                            onChange={e => onProductSelect(e, rec)} name={rec['product_code']} />}
                           />                     */}
                        <b>{i + 1}. {rec.product_name} </b> <br/>
                        <i>{rec && rec.additional_details && rec.additional_details.AnnuityOption && (
                          <>{rec.additional_details.AnnuityOption}</>
                        )}</i>
                      {/* <Button onClick={()=>{toggle_expand_section(i)}} variant="outlined" color="primary" style={{marginLeft:20, right:0, float: 'right'}}><ExpandMore  id={`toggle_expand_button_icon_${i}`}/><span id={`toggle_expand_button_${i}`}>Expand</span></Button>                       */}
                    {/* <div style={{display:'none'}} id={`expand_section_${i}`}> */}
                    <div id={`expand_section_${i}`}>

                      <Box p={2}>
                        {/* <Typography>Benefit Type: {rec.benefit_type}</Typography> */}
                        {/* <Typography>Risk Profile: {getRowRisk(rec) || '-'}</Typography> */}
                        <Typography><b>Annual Premium: </b>₹{num(rec.annual_premium)}</Typography>
                        <Typography><b>Policy Term:</b> {rec.policy_term} yrs</Typography>
                        <Typography><b>PPT: </b>{rec.premium_payment_term} yrs</Typography>
                        {/* <Typography>Target Value: ₹{num(rec.target_value)}</Typography>
                        <Typography>Calculated Benefit: ₹{num(rec.calculated_benefit)}</Typography> */}
                        <Typography></Typography>
                        {rec.fund_value && Object.keys(rec.fund_value).length > 0 ? (<TableContainer component={Paper}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Year</strong></TableCell>
                                <TableCell><strong>Fund Value</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {rec.fund_value && Object.entries(rec.fund_value).map(([key, value]) => (
                                <TableRow key={key}>
                                  <TableCell>{key}</TableCell>
                                  <TableCell>{value}</TableCell>
                                </TableRow>))}
                              <tr></tr>
                            </TableBody>

                          </Table>
                        </TableContainer>) : (
                          <></>
                        )}
                        {/* Uncomment if you want to see which API delivered the row: 
                  <Typography variant="caption" color="text.secondary">Source: {rec._source}</Typography> 
                  */}
                        {/* {rec.additional_details && Object.keys(rec.additional_details).length > 0 && (
                          <Accordion sx={{ mt: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                              <Typography fontWeight="bold">View Additional Details</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              {Object.entries(rec.additional_details).map(([k, v]) => (
                                <Box key={k} display="flex" justifyContent="space-between" mb={1}>
                                  <Typography fontWeight="bold">{k}</Typography><Typography>{String(v)}</Typography>
                                </Box>
                              ))}
                            </AccordionDetails>
                          </Accordion>
                        )} */}
                      </Box>
                    </div>
                      <Button onClick={()=>{openLoginAssistant(rec.product_code)}} variant="contained" color="primary">Generate Quote</Button>&nbsp;
                      <Button onClick={()=>{downloadPdfProduct(rec.xml_details)}} variant="outlined" color="primary">Download EBI</Button>

                    </CardContent>
                  </Card>
                ))}
                {/* loading-more indicator while other APIs are still running */}
                {pendingRequests > 0 && filteredRecs.length > 0 && (
                  <Box display="flex" justifyContent="center" alignItems="center" mt={2} gap={1}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading more…</Typography>
                  </Box>
                )}
              </Box>
              {/* ================ ERRORS ================ */}
              {/* {Array.isArray(recommendations.errors) && recommendations.errors.length > 0 && (
                <Accordion sx={{ mt: 4 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6" fontWeight="bold">Errors / Unavailable Scenarios:</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {recommendations.errors.map((err, i) => (
                      <Card key={i} sx={{ mb: 2, borderRadius: 2, bgcolor: '#ffe9e9' }}>
                        <CardContent>
                          <Typography fontWeight="bold" color="error" title={err.product_code}>
                            {(err.product_name ?? err['product_name'] ?? 'Unknown')} – {err.benefit_type ?? '-'}
                          </Typography>
                          <Typography>{err.error_message ?? err.message ?? 'Unknown error'}</Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </AccordionDetails>
                </Accordion>
              )} */}
              {/* ================ SEGMENT DATA CHECKBOXES ================ */}
              {/* {Array.isArray(segmentData) && segmentData.length > 0 && (
                <Box mt={4}>
                  <Typography variant="h6" fontWeight="bold">Select Final Product(s) for Generating Pitch</Typography>
                  <Typography variant="body2" mb={2}>(Choose from segment data or recommended ones)</Typography>
                  <FormControl component="fieldset" fullWidth>
                    <FormGroup>
                      {segmentData.map(p => (
                        <FormControlLabel key={p['Product Code']}
                          control={<Checkbox checked={selectedProductsState.includes(p['Product Name'])}
                            onChange={e => onProductSelect(e, p)} name={p['Product Name']} />}
                          label={p['Product Name']} />
                      ))}
                    </FormGroup>
                  </FormControl>
                </Box>
              )} */}

              {/* ================ NEXT BTN ================ */}
            </Paper>
          </Grid>



          {condition_added == 1 ? (
          <Grid item xs={12} md={6} mt={3} >
            <h1>
              Second Recommendation
            </h1>
            <Paper elevation={1} sx={{ p: 2, mb: 1, border: '1px solid #ddd', borderRadius: 2, bgcolor: '#f9f9f9' }}>
              {/* ================ SINGLE / MIX CHOICE ================ */}
              {showChoice && (
                <Box mt={4}>
                  <Typography variant="h6">Please choose Single Product or Product Mix:</Typography>
                  <Box display="flex" gap={2} mt={2}>
                    {[
                      { label: 'Single Product', type: '1.1' },
                      { label: 'Product Mix', type: '1.2' }
                    ].map(btn => (
                      <Card key={btn.type} onClick={() => onRecType(btn.type)}
                        sx={{
                          flex: 1, cursor: 'pointer', borderRadius: 2,
                          border: chosenType === btn.type ? '4px solid #1976d2' : '1px solid grey'
                        }}>
                        <CardContent><Typography fontWeight="bold">{btn.label}</Typography></CardContent>
                      </Card>
                    ))}
                  </Box>
                </Box>
              )}
              {/* ================ SINGLE / MIX CONTENT ================ */}
              {chosenType === '1.1' && singleExists && (
                <Box mt={4}>
                  <Typography variant="h6" mb={2}>Single Product Recommendation</Typography>
                  <Card sx={{ borderRadius: 2 }}><CardContent>
                    <Typography component="div" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(recommendations.single_product_recommendation) }} />
                  </CardContent></Card>
                </Box>
              )}
              {chosenType === '1.2' && mixExists && (
                <Box mt={4}>
                  <Typography variant="h6" mb={2}>Product Mix Recommendation</Typography>
                  <Card sx={{ borderRadius: 2 }}><CardContent>
                    <Typography component="div" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(recommendations.product_mix_recommendation) }} />
                  </CardContent></Card>
                </Box>
              )}
              {/* ================ SYSTEM RECOMMENDATIONS ================ */}
              <Box mt={6}>
                <Typography variant="h5" fontWeight="bold">System Recommendations (Premium – lowest first)</Typography>
                {/* Empty state while loading */}
                {pendingRequests2 > 0 && (filteredRecs2.length === 0) && (
                  <Box display="flex" alignItems="center" gap={1} mt={2}>
                    <CircularProgress size={20} /><Typography variant="body2">Loading…</Typography>
                  </Box>
                )}
                {/* No matches after load */}
                {pendingRequests2 === 0 && filteredRecs2.length === 0 && (
                  <Box mt={2}>

                    <Typography variant="body2">No recommendations match the current filters.</Typography>
                    <Button onClick={resetFilters} size="small" sx={{ mt: 1 }}>Reset Filters</Button>
                  </Box>
                )}
                {filteredRecs2.slice(0,3).map((rec, i) => (
                  <Accordion key={i} sx={{ mb: 2 }}>
                                            {/* <FormControlLabel key={p['Product Code']}
                          control={<Checkbox checked={selectedProductsState.includes(p['Product Name'])}
                            onChange={e => onProductSelect(e, p)} name={p['Product Name']} />}
                          label={p['Product Name']} /> */}

                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography >
                        <b>{i + 1}. {rec.product_name} </b> (Annualssss Premiumsssss: ₹{num(rec.annual_premium)})
                        <br />
                        <i>{rec && rec.additional_details && rec.additional_details.AnnuityOption && (
                          <>{rec.additional_details.AnnuityOption}</>
                        )}</i>

                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box p={2}>
                        <Typography>Benefit Type: {rec.benefit_type}</Typography>
                        <Typography>Risk Profile: {getRowRisk(rec) || '-'}</Typography>
                        <Typography>Annual Premium: ₹{num(rec.annual_premium)}</Typography>
                        <Typography>Policy Term: {rec.policy_term} yrs</Typography>
                        <Typography>PPT: {rec.premium_payment_term} yrs</Typography>
                        <Typography>Target Value: ₹{num(rec.target_value)}</Typography>
                        <Typography>Calculated Benefit: ₹{num(rec.calculated_benefit)}</Typography>
                        <Typography></Typography>
                        {rec.fund_value && Object.keys(rec.fund_value).length > 0 ? (<TableContainer component={Paper}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Year</strong></TableCell>
                                <TableCell><strong>Fund Value</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {rec.fund_value && Object.entries(rec.fund_value).map(([key, value]) => (
                                <TableRow key={key}>
                                  <TableCell>{key}</TableCell>
                                  <TableCell>{value}</TableCell>
                                </TableRow>))}
                              <tr></tr>
                            </TableBody>

                          </Table>
                        </TableContainer>) : (
                          <></>
                        )}
                        {/* Uncomment if you want to see which API delivered the row: 
                  <Typography variant="caption" color="text.secondary">Source: {rec._source}</Typography> 
                  */}
                        {rec.additional_details && Object.keys(rec.additional_details).length > 0 && (
                          <Accordion sx={{ mt: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                              <Typography fontWeight="bold">View Additional Details</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              {Object.entries(rec.additional_details).map(([k, v]) => (
                                <Box key={k} display="flex" justifyContent="space-between" mb={1}>
                                  <Typography fontWeight="bold">{k}</Typography><Typography>{String(v)}</Typography>
                                </Box>
                              ))}
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
                {/* loading-more indicator while other APIs are still running */}
                {pendingRequests2 > 0 && filteredRecs2.length > 0 && (
                  <Box display="flex" justifyContent="center" alignItems="center" mt={2} gap={1}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading more…</Typography>
                  </Box>
                )}
              </Box>
              {/* ================ ERRORS ================ */}
              {Array.isArray(recommendations2.errors) && recommendations2.errors.length > 0 && (
                <Accordion sx={{ mt: 4 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6" fontWeight="bold">Errors / Unavailable Scenarios:</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {recommendations2.errors.map((err, i) => (
                      <Card key={i} sx={{ mb: 2, borderRadius: 2, bgcolor: '#ffe9e9' }}>
                        <CardContent>
                          <Typography fontWeight="bold" color="error" title={err.product_code}>
                            {(err.product_name ?? err['product_name'] ?? 'Unknown')} – {err.benefit_type ?? '-'}
                          </Typography>
                          <Typography>{err.error_message ?? err.message ?? 'Unknown error'}</Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </AccordionDetails>
                </Accordion>
              )}
              {/* ================ SEGMENT DATA CHECKBOXES ================ */}
              {Array.isArray(segmentData2) && segmentData2.length > 0 && (
                <Box mt={4}>
                  <Typography variant="h6" fontWeight="bold">Select Final Product(s) for Product Comparisons</Typography>
                  <Typography variant="body2" mb={2}>(Choose from segment data or recommended ones)</Typography>
                  <FormControl component="fieldset" fullWidth>
                    <FormGroup>
                      {segmentData2.map(p => (
                        <FormControlLabel key={p['Product Code']}
                          control={<Checkbox checked={selectedProductsState.includes(p['Product Name'])}
                            onChange={e => onProductSelect(e, p)} name={p['Product Name']} />}
                          label={p['Product Name']} />
                      ))}
                    </FormGroup>
                  </FormControl>
                </Box>
              )}

              {/* ================ NEXT BTN ================ */}
              {(segmentData2?.length > 0 || filteredRecs2.length > 0) && (
                <>
              </>
              )}
            </Paper>
          </Grid>
          ):(<></>)}

          <Grid item xs={12} md={12} mt={3} >

            {((segmentData?.length > 0 || filteredRecs.length > 0) || (segmentData2?.length > 0 || filteredRecs2.length > 0)) && (
             <></>
            )}
          </Grid>

        </Grid>



      </motion.div>
    </Container>
  );
} 